import { useMemo } from 'react';
import { useTranslation } from '@lib/useTypedTranslation';
import { IFilterResult } from '@components/filters/filterLib';
import { groupFilterValues, homeLocationFilterValues } from '@components/filters/devicesFilterLib';
import { useGenericFilterOptions } from '../../../../../components/data-table/filters/useGenericFilterOptions';
import { omitShallow } from '@lib/omitShallow';
import { ITranslationKeys } from '@components/i18n/keys';
import { getGroups, getHomeLocations, getManufacturers, getModels, IGroup, IGroups, IHomeLocation } from '../../../../../services/config/config';

export type DeviceAnomaliesFilterData = [IGroups, string[], string[], IHomeLocation[]];

const deviceAnomaliesListFilters = ['manufacturers', 'models', 'groups', 'homeLocations', 'platforms'] as const;
type DeviceAnomaliesListFilter = typeof deviceAnomaliesListFilters[number];

function getInitialAvailableDeviceAnomaliesFilters(hiddenFilters: DeviceAnomaliesListFilter[]): IFilterResult {
  const initialAvailableFilters: IFilterResult = {
    manufacturers: [],
    models: [],
    groups: [],
    homeLocations: [],
    platforms: []
  };

  return omitShallow(initialAvailableFilters, ...hiddenFilters);
}

function createDeviceAnomaliesFilterProcessor(t: any) {
  return (data: DeviceAnomaliesFilterData, hiddenFilters: string[]): IFilterResult => {
    const homeLocationFilterVisible = !hiddenFilters.includes('homeLocations');

    const translatedGroupFilterValues = groupFilterValues.map(val => ({
      ...val,
      value: t(val.value as keyof ITranslationKeys['translation'], { ns: 'translation' })
    }));

    const translatedHomeLocationFilterValues = homeLocationFilterValues.map(val => ({
      ...val,
      value: t(val.value as keyof ITranslationKeys['translation'], { ns: 'translation' })
    }));

    const groupsRaw = data[0];
    const models = data[1];
    const manufacturers = data[2];

    const groups = [
      ...translatedGroupFilterValues,
      ...(groupsRaw?.groups?.nodes ?? []).map((o: IGroup) => o.name)
    ];

    const platforms = ['android', 'windows'];

    const availableFilters: IFilterResult = {
      groups,
      models,
      manufacturers,
      platforms
    };

    if (homeLocationFilterVisible) {
      const homeLocationsRaw = data[3];
      const homeLocations = homeLocationsRaw.map((o) => ({ id: o.id, value: o.name }));
      availableFilters.homeLocations = [...translatedHomeLocationFilterValues, ...homeLocations];
    }

    return availableFilters;
  };
}

export const useDeviceAnomaliesFilterOptions = (
  hiddenFilters: DeviceAnomaliesListFilter[] = []
) => {
  const { t } = useTranslation(['translation', 'filters']);

  const homeLocationFilterVisible = useMemo(() => {
    return !hiddenFilters.includes('homeLocations');
  }, [hiddenFilters]);

  const dataProcessor = useMemo(() => {
    return createDeviceAnomaliesFilterProcessor(t);
  }, [t]);

  const dataFetchers = useMemo(() => {
    const fetchers: any[] = [
      getGroups,
      getModels,
      getManufacturers,
    ];
    if (homeLocationFilterVisible) {
      fetchers.push(getHomeLocations);
    }
    return fetchers;
  }, [homeLocationFilterVisible]);

  const { availableFilters, filtersLoaded } = useGenericFilterOptions({
    hiddenFilters,
    dataFetchers,
    dataProcessor,
    getInitialFilters: getInitialAvailableDeviceAnomaliesFilters
  });

  return {
    availableFilters,
    filtersLoaded
  };
};
